import { type OrderData } from "~/domains/orders/types/modelsV2";
import { produce } from "immer";
import { useReducer } from "react";
import { isErrorEntry } from "~/features/orders/common/utils/util";

export interface DocumentListRow<T> {
  data: T;
  detailsLoaded: boolean;
  loadingDetails: boolean;
  errorLoadingDetails: boolean;
  open: boolean;
}

export function canStartLoadingDetails(item: DocumentListRow<unknown>) {
  return !item.detailsLoaded && !item.loadingDetails && !item.errorLoadingDetails;
}

export function useOrderListState(): [
  DocumentListPageState<OrderData>,
  (action: UseOrderListStateActionType) => void,
  typeof orderListPageActions,
] {
  return [...useReducer(orderListPageReducer, initialState), orderListPageActions];
}

interface DocumentListPageState<T> {
  loading: boolean;
  error: boolean;
  results: DocumentListRow<T>[];
}

const initialState = {
  loading: true,
  error: false,
  results: [],
};

function beginLoading() {
  return {
    type: "beginLoading",
  } as const;
}

function addResultPage<T>(data: T[]) {
  return {
    type: "loadPage",
    payload: data,
  } as const;
}

function finishLoading() {
  return {
    type: "finishLoading",
  } as const;
}

function errorLoading() {
  return {
    type: "errorLoading",
  } as const;
}

export function finishLoadingDetails(data: OrderData) {
  return {
    type: "finishLoadingDetails",
    payload: {
      data,
    },
  } as const;
}

function errorLoadingDetails(soNumber: string) {
  return {
    type: "errorLoadingDetails",
    payload: soNumber,
  } as const;
}

function toggleOpen(soNumber: string) {
  return {
    type: "toggleOpen",
    payload: { soNumber, open },
  } as const;
}

function toggleAllOpen() {
  return {
    type: "toggleAllOpen",
    payload: open,
  } as const;
}

export const orderListPageActions = {
  beginLoading,
  addResultPage,
  finishLoading,
  errorLoading,
  finishLoadingDetails,
  errorLoadingDetails,
  toggleOpen,
  toggleAllOpen,
};

export type UseOrderListStateActionType =
  | ReturnType<typeof beginLoading>
  | ReturnType<typeof addResultPage>
  | ReturnType<typeof finishLoading>
  | ReturnType<typeof errorLoading>
  | ReturnType<typeof finishLoadingDetails>
  | ReturnType<typeof errorLoadingDetails>
  | ReturnType<typeof toggleOpen>
  | ReturnType<typeof toggleAllOpen>;

export const orderListPageReducer = function <T extends UseOrderListStateActionType = UseOrderListStateActionType>(
  state: DocumentListPageState<OrderData>,
  action: T,
) {
  return produce(state, (draft) => {
    switch (action.type) {
      case "beginLoading": {
        draft.results = [];
        draft.loading = true;
        draft.error = false;
        break;
      }

      case "loadPage": {
        const newRows: DocumentListRow<OrderData>[] = action.payload.map((unknownData) => {
          const data = unknownData as OrderData;
          return {
            data,
            loadingDetails: false,
            errorLoadingDetails: false,
            detailsLoaded: false,
            open: false,
          };
        });
        draft.results.push(...newRows);
        break;
      }

      case "finishLoading": {
        draft.loading = false;
        break;
      }

      case "errorLoading": {
        draft.loading = false;
        draft.error = true;
        break;
      }

      case "finishLoadingDetails": {
        const details = action.payload.data;
        const row = draft.results.find((item) => item.data.soNumber === details.soNumber);
        if (!row) {
          break;
        }
        row.data = details;
        row.loadingDetails = false;
        row.detailsLoaded = true;
        break;
      }

      case "errorLoadingDetails": {
        const soNumber = action.payload;
        const row = draft.results.find((item) => item.data.soNumber === soNumber);
        if (!row) {
          break;
        }
        row.loadingDetails = false;
        row.errorLoadingDetails = true;
        break;
      }

      case "toggleOpen": {
        const { soNumber } = action.payload;
        const item = draft.results.find((item) => item.data.soNumber === soNumber)!;
        const newVal = !item.open;
        item.open = newVal;
        if (newVal && canStartLoadingDetails(item)) {
          item.loadingDetails = true;
        }
        break;
      }

      case "toggleAllOpen": {
        const newValue = !draft.results.every((item) => item.open);
        draft.results.forEach((item) => {
          item.open = newValue;
          if (!isErrorEntry(item)) {
            if (newValue && canStartLoadingDetails(item)) {
              item.loadingDetails = true;
            }
          }
        });
        break;
      }
    }
  });
};
